/**
 * 購物車「公司戶發票」提醒 + 企業會員統編自動帶入。
 *
 * 商業規則：企業會員（同時具備公司名稱與統一編號）自動帶入並鎖定欄位；
 * 其餘身分仍可自行選擇公司戶發票並手動填寫統編，僅在該頁首次點選時彈窗提醒可註冊企業會員（不阻擋、不強制）。
 *
 * 以 ES module 載入（見 sections/main-cart.liquid）。純函式與 factory 皆具名匯出，
 * 讓行為可在不啟動整頁的情況下被單元測試覆蓋。
 */

const SELECTORS = Object.freeze({
  companyInvoiceOption: 'li[data-invoice="company"]',
  companyNameInput: 'input[name="ako-company-name"]',
  companyCodeInput: 'input[name="ako-company-code"]',
  invoiceMenuButton: '.ako-menu-button',
  invoiceMenuList: 'ul.ako-menu-list',
  invoiceMenuScope: 'li, div, .ako-menu, form',
})

const ACCOUNT_INFO_ENDPOINT = 'https://shopify-serve.msi.com/api/getAccountInfo/tw'
const ENTERPRISE_REGISTER_PATH = 'https://business.msi.com/zh-Hant/member/signup'

const TIMING = Object.freeze({
  fieldWaitMs: 3000,
  optionReadyMs: 1000,
  optionRetryMs: 200,
  optionMaxAttempts: 10,
})

/** #595959 於 #f0f0f0 上對比約 7:1，符合 WCAG 2.1 AA (1.4.3)。 */
const LOCKED_STYLE = Object.freeze({
  backgroundColor: '#f0f0f0',
  color: '#595959',
})

const MESSAGES = Object.freeze({
  modalAriaLabel: '企業會員資格確認',
  modalTitle: '有企業採購需求？加入 MSI 企業會員享專屬權益',
  modalDescription:
    '現在起免費申請 MSI 企業會員，1分鐘享有企業專屬優惠價、自動帶入統編不怕漏開發票、累計採購獎勵及多種企業採購服務喔!',
  modalDismiss: '暫時不用，繼續結帳',
  modalRegister: '前往註冊企業會員',
})

const readTrimmed = (value) =>
  typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * 只保留流程必要的兩個欄位，API 回傳的其餘個資不進入應用層（GDPR 資料最小化）。
 */
export function toEnterpriseProfile(payload) {
  if (!payload || typeof payload !== 'object') return null

  const companyName = readTrimmed(payload.company_name)
  const vatNumber = readTrimmed(payload.vat_number)

  return companyName && vatNumber ? Object.freeze({ companyName, vatNumber }) : null
}

export function waitForElement(selector, { timeoutMs = TIMING.fieldWaitMs, root = document } = {}) {
  return new Promise((resolve, reject) => {
    const existing = root.querySelector(selector)
    if (existing?.isConnected) return resolve(existing)

    const observer = new MutationObserver(() => {
      const element = root.querySelector(selector)
      if (!element?.isConnected) return
      cleanup()
      resolve(element)
    })

    const timer = setTimeout(() => {
      cleanup()
      reject(new Error(`Timed out waiting for ${selector}`))
    }, timeoutMs)

    function cleanup() {
      clearTimeout(timer)
      observer.disconnect()
    }

    observer.observe(root.body ?? root, { childList: true, subtree: true })
  })
}

/**
 * 企業會員資格查詢：全頁共用同一個 in-flight promise，避免重複送出含 cookie 的請求。
 */
export function createEnterpriseAccountClient({
  endpoint = ACCOUNT_INFO_ENDPOINT,
  fetchImpl = (...args) => globalThis.fetch(...args),
} = {}) {
  let pending = null

  return {
    getProfile() {
      if (!pending) {
        pending = fetchImpl(endpoint, {
          credentials: 'include',
          cache: 'no-store', // 回應含個資，禁止落入瀏覽器快取
          headers: { Accept: 'application/json' },
        })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => {
            // 僅記錄失敗事實，不輸出回應內容或錯誤細節，避免個資進入 console
            console.warn('[company-invoice] 企業會員資格查詢失敗，將以非企業會員處理。')
            return null
          })
          .then(toEnterpriseProfile)
      }
      return pending
    },
  }
}

function buildModalContent() {
  const wrapper = document.createElement('div')
  wrapper.className = 'account--modal-content'

  const title = document.createElement('h2')
  title.className = 'account--modal-title'
  title.textContent = MESSAGES.modalTitle

  const description = document.createElement('p')
  description.className = 'account--modal-description-text'
  description.textContent = MESSAGES.modalDescription

  wrapper.append(title, description)
  return wrapper
}

/**
 * 沿用 MSIIslands 共用 modal（focus trap、Esc、aria-modal、關閉後焦點還原皆由其負責）。
 * 內容以 DOM 節點 + textContent 建立，不走 HTML 字串，杜絕 XSS。
 */
export function openEnterpriseInviteModal({
  createModal = globalThis.MSIIslands?.createModal,
  navigate = (path) => window.location.assign(path),
} = {}) {
  if (typeof createModal !== 'function') {
    // 降級路徑：仍保留使用者選擇權，不強制導轉
    console.warn('[company-invoice] MSIIslands.createModal 不可用，改用原生對話框降級。')
    if (window.confirm(`${MESSAGES.modalTitle}\n\n${MESSAGES.modalDescription}`)) {
      navigate(ENTERPRISE_REGISTER_PATH)
    }
    return
  }

  createModal({
    ariaLabel: MESSAGES.modalAriaLabel,
    closeOnBackdrop: true,
    content: buildModalContent(),
    actions: [
      {
        role: 'cancel',
        label: MESSAGES.modalDismiss,
        className: 'account--modal-btn account--modal-btn-secondary',
      },
      {
        id: 'enterprise-register',
        label: MESSAGES.modalRegister,
        className: 'account--modal-btn account--modal-btn-primary',
        onClick(_event, modalApi) {
          modalApi.close()
          navigate(ENTERPRISE_REGISTER_PATH)
        },
      },
    ],
  }).open()
}

/**
 * 公司戶發票的提醒點：不攔截選取行為，僅在本次頁面生命週期內對非企業會員提醒一次。
 * 刻意只用記憶體旗標（不寫 cookie／storage），使用者重新整理或重新進站可再被提醒一次。
 */
export function createCompanyInvoiceGate({
  accountClient,
  onPrompt = openEnterpriseInviteModal,
  root = document,
} = {}) {
  const controller = new AbortController()
  let isEnterpriseVerified = false
  let hasPrompted = false

  const selectOption = (option) => option.click()

  root.addEventListener(
    'click',
    (event) => {
      if (isEnterpriseVerified || hasPrompted) return
      if (!event.target.closest?.(SELECTORS.companyInvoiceOption)) return

      // 同步標記，避免連續點擊在資格查詢回應前重複彈窗
      hasPrompted = true

      accountClient.getProfile().then((profile) => {
        if (profile) {
          isEnterpriseVerified = true
          return
        }
        onPrompt()
      })
    },
    { capture: true, signal: controller.signal },
  )

  return {
    markVerified() {
      isEnterpriseVerified = true
    },
    selectOption,
    destroy() {
      controller.abort()
    },
    get signal() {
      return controller.signal
    },
  }
}

/**
 * 主題的發票選單是非同步渲染的，點擊後欄位不一定立即存在，故採有限次數重試。
 */
async function openCompanyInvoicePanel(gate) {
  for (let attempt = 0; attempt < TIMING.optionMaxAttempts; attempt += 1) {
    const option = document.querySelector(SELECTORS.companyInvoiceOption)
    if (option) gate.selectOption(option)

    try {
      await waitForElement(SELECTORS.companyNameInput, { timeoutMs: TIMING.optionReadyMs })
      return true
    } catch {
      await delay(TIMING.optionRetryMs)
    }
  }
  return false
}

/** readOnly 而非 disabled：值仍會隨表單送出，且欄位保持可聚焦、可複製、可被螢幕閱讀器讀取。 */
function lockField(input, value) {
  input.value = value
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.readOnly = true
  Object.assign(input.style, LOCKED_STYLE)
}

async function fillEnterpriseFields(profile) {
  const fields = [
    [SELECTORS.companyNameInput, profile.companyName],
    [SELECTORS.companyCodeInput, profile.vatNumber],
  ]

  for (const [selector, value] of fields) {
    lockField(await waitForElement(selector), value)
  }
}

function closeInvoiceMenu(button) {
  button.setAttribute('aria-expanded', 'false')

  const scope = button.closest(SELECTORS.invoiceMenuScope) ?? document
  const scoped = scope.querySelectorAll(SELECTORS.invoiceMenuList)
  const lists = scoped.length ? scoped : document.querySelectorAll(SELECTORS.invoiceMenuList)

  lists.forEach((list) => {
    list.hidden = true
    list.style.display = 'none'
  })
}

/** 企業會員的發票類型已由系統決定，鎖定選單避免被改回其他類型。 */
function lockInvoiceTypeMenu(signal) {
  // 主題可能於鎖定後重新渲染出可用按鈕，保留 capture 攔截作為第二道防線
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.(SELECTORS.invoiceMenuButton)
      if (!button) return

      event.preventDefault()
      event.stopImmediatePropagation()
      closeInvoiceMenu(button)
    },
    { capture: true, signal },
  )

  document.querySelectorAll(SELECTORS.invoiceMenuButton).forEach((button) => {
    closeInvoiceMenu(button)
    button.disabled = true
    button.setAttribute('aria-disabled', 'true')
    Object.assign(button.style, LOCKED_STYLE, { cursor: 'not-allowed' })
  })
}

export async function startCompanyInvoiceFlow({
  accountClient = createEnterpriseAccountClient(),
  gate,
} = {}) {
  const activeGate = gate ?? createCompanyInvoiceGate({ accountClient })

  try {
    const profile = await accountClient.getProfile()
    if (!profile) return activeGate

    activeGate.markVerified()

    if (!(await openCompanyInvoicePanel(activeGate))) {
      console.warn('[company-invoice] 公司戶發票欄位未出現，略過自動帶入。')
      return activeGate
    }

    await fillEnterpriseFields(profile)
    lockInvoiceTypeMenu(activeGate.signal)
  } catch {
    // 不輸出錯誤物件，避免夾帶欄位值等個資
    console.warn('[company-invoice] 自動帶入流程未完成。')
  }

  return activeGate
}

if (typeof document !== 'undefined') {
  startCompanyInvoiceFlow()
}
